import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { useLazyGetCustomersInfoDictionariesQuery } from '../../../../store/dictionaries/services';
import {
  CorporateFormValues,
  NewApplicationListItem,
} from '../../../new-application/types';
import { useCreateCorporateApplicationMutation } from '../../../new-application/store/services';
import { setHintVisible } from '../../store/slices/list';

import ROUTES from '../../../../routes';

const schema = {
  type: 'object',
  properties: {
    countryOfIncorporationId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Country Of Incorporation is required',
    },
    companyName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Company Name is required',
    },
    deliveryChannelId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Delivery Channel is required',
    },
  },
  required: ['countryOfIncorporationId', 'companyName', 'deliveryChannelId'],
};

const defaultFormValues = {
  countryOfIncorporationId: '',
  companyName: '',
};

const useCreate = ({
  activePermissions,
}: {
  activePermissions?: PermissionEnum[];
}) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [applicationSchemeVisible, setApplicationSchemeVisible] =
    useState(false);

  const [getInfoDictionaries] = useLazyGetCustomersInfoDictionariesQuery();
  const [create, { isLoading: loading, isSuccess: submitIsSuccess }] =
    useCreateCorporateApplicationMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setError,
  } = useForm<CorporateFormValues>({
    defaultValues: defaultFormValues,
    resolver: ajvResolver(schema),
  });

  const handleClose = () => {
    setPanelIsOpen(false);

    reset(defaultFormValues);

    setTimeout(() => {
      navigate(`${ROUTES.CUSTOMERS_CORPORATE_LIST}${search || ''}`);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: CorporateFormValues = {
      countryOfIncorporationId: values.countryOfIncorporationId,
      companyName: values.companyName,
      deliveryChannelId: values.deliveryChannelId,
    };

    if (values.applicationSchemeId) {
      body.applicationSchemeId = values.applicationSchemeId;
    }

    const response = (await create(body)) as {
      data?: { data: NewApplicationListItem };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (submitIsSuccess) {
      handleClose();

      setTimeout(() => {
        dispatch(setHintVisible(true));
      }, 100);
    }
  }, [dispatch, submitIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_CREATE_ACTION,
      )
    ) {
      getInfoDictionaries();
    }
  }, [activePermissions]);

  return {
    control,
    errors,
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    loading,
    onSubmit,
    dirtyPopupVisible,
    applicationSchemeVisible,
    setApplicationSchemeVisible,
  };
};

export default useCreate;
